import { User, BirdieGameGroup, getManualPlayerId } from "@/types";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CreateGameForm } from "@/components";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCourses } from "@/hooks/useCourses";

interface ProgressBarsProps {
  golfers: User[];
  eligibleGames: BirdieGameGroup[];
  courseId: string;
  refreshKey?: number;
  totalHoles?: number;
}

const ProgressBars = ({ golfers, eligibleGames, courseId, refreshKey = 0, totalHoles = 18 }: ProgressBarsProps) => {
  const [showCreateGameModal, setShowCreateGameModal] = useState(false);
  const [birdieData, setBirdieData] = useState<{[key: string]: number[]}>({});
  const [isLoading, setIsLoading] = useState(false);
  const { allCourses, loading: isLoadingCourses } = useCourses();

  useEffect(() => {
    const fetchBirdieData = async () => {
      setIsLoading(true);
      const newBirdieData: {[key: string]: number[]} = {};

      try {
        for (const golfer of golfers) {
          const mpId = getManualPlayerId(golfer.id);
          let query = supabase
            .from('scores')
            .select('hole_number')
            .eq('course_id', courseId);
          query = mpId
            ? query.eq('manual_player_id', mpId)
            : query.eq('user_id', golfer.id).is('manual_player_id', null);
          const { data: scores, error } = await query;

          if (error) {
            console.error("Error fetching birdie data:", error);
            continue;
          }

          const uniqueHoles = [...new Set(scores?.map(s => s.hole_number) || [])];
          newBirdieData[golfer.id] = uniqueHoles;
        }

        setBirdieData(newBirdieData);
      } catch (error) {
        console.error("Error in fetchBirdieData:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBirdieData();
  }, [golfers, courseId, refreshKey]);

  const handleCreateGame = () => {
    setShowCreateGameModal(true);
  };

  const handleGameCreated = () => {
    setShowCreateGameModal(false);
    toast.success("Game Created Successfully", {
      description: "Your new birdie game has been created with the current golfers."
    });
  };

  const trulyEligibleGames = eligibleGames.filter(game =>
    game.members.every(member =>
      golfers.some(golfer => golfer.id === member.id)
    )
  );

  const hasGameWithAllGolfers = trulyEligibleGames.some(game =>
    game.members.length === golfers.length &&
    golfers.every(golfer =>
      game.members.some(member => member.id === golfer.id)
    )
  );

  const isSingleGolferWithNoExactMatch = golfers.length === 1 &&
    !trulyEligibleGames.some(game => game.members.length === 1 && game.members[0].id === golfers[0].id);

  const shouldShowCurrentGroup = !hasGameWithAllGolfers || isSingleGolferWithNoExactMatch;

  return (
    <div className="space-y-4 mb-6">
      <div>
        <h3 className="text-sm font-medium mb-2">Eligible Games</h3>
        <Accordion type="single" collapsible className="w-full">
          {shouldShowCurrentGroup && (
            <AccordionItem value="current-group" className="border-b border-border py-1">
              <AccordionTrigger className="py-2 hover:no-underline">
                <div className="flex justify-between items-center w-full pr-2">
                  <span className="text-sm font-medium">Current Group</span>
                  <span className="text-forest text-xs font-medium">
                    {golfers.length} Golfer{golfers.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-1 pb-2">
                  {golfers.map(golfer => {
                    const birdiedHoles = birdieData[golfer.id] || [];
                    const progress = Math.round((birdiedHoles.length / totalHoles) * 100);

                    return (
                      <div key={golfer.id} className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-medium">{golfer.name}</span>
                          <span>{birdiedHoles.length}/{totalHoles} holes</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>
                    );
                  })}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 text-xs bg-forest/5 border-forest/20 text-forest hover:bg-forest/10"
                    onClick={handleCreateGame}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Create Game for Group
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {trulyEligibleGames.map(game => {
            return (
              <AccordionItem key={game.id} value={game.id} className="border-b border-border py-1">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex justify-between items-center w-full pr-2">
                    <span className="text-sm font-medium">{game.name} ({game.year})</span>
                    <span className="text-forest text-xs font-medium">
                      {game.members.length} Golfer{game.members.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-1 pb-2">
                    {game.members.map(golfer => {
                      const birdiedHoles = birdieData[golfer.id] || [];
                      const progress = Math.round((birdiedHoles.length / totalHoles) * 100);

                      return (
                        <div key={golfer.id} className="space-y-1">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-medium">{golfer.name}</span>
                            <span>{birdiedHoles.length}/{totalHoles} holes</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <Dialog open={showCreateGameModal} onOpenChange={setShowCreateGameModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <div className="py-2">
            <h2 className="text-xl font-semibold mb-4">Create New Game</h2>
            <CreateGameForm
              courses={allCourses}
              onCreateGame={handleGameCreated}
              isLoadingCourses={isLoadingCourses}
              defaultCourseId={courseId}
              defaultSelectedFriends={golfers}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgressBars;
